import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  Pill,
  SearchBar,
  TableActionTypes,
  TableState,
  Typography,
  MUIDataTableColumn
} from 'web-library';
import React, { useEffect, useMemo, useState } from 'react';


import { makeStyles } from 'tss-react/mui';
import { MUIDataTableCustomFilterListOptions } from 'web-library/stories/organism/DataTable/DataTable';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { stringToLongDateAndShortTime } from '../../../utils/DateUtils';
import { AccountTypeMode } from './index';
import { usePostAccountManagementListing } from '../../../api-refactored/account-management/usePostAccountManagementListing';
import { customFilterChipRender } from '../../../hooks/table';
import AccountStatusMultiSelect from '../../../components/select/AccountStatusMultiSelect';
import { KeyValueResponse } from '../../../api-refactored/typings';
import { CodeValueResponse } from '../../../api';
import OrganizationMultiSelect from '../../../components/select/OrganizationMultiSelect';
import RouteKey from '../../../routes/key';

const { adminAccountManagement, userAccountManagement, viewDetails } = RouteKey.admin.child;

const useStyles = makeStyles()((theme) =>
({
  datatableContainer: {
    marginBottom: theme.spacing(7),
  },
  searchContainer: {
    marginBottom: theme.spacing(3),
  },
  nricUserNameText: {
    color: theme.color.text.textTertiary,
  },
  pill: {
    marginBottom: theme.spacing(2.5),
  },
  viewDetailsButton: {
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  select: {
    marginBottom: theme.spacing(3),
  },
}));

const tableColumns = (
  hooks: {
    selectedRequestTypeMode: AccountTypeMode;
    state: TableState;
    classes: Record<string,string>;
    t: TFunction;
    history: RouteComponentProps['history'];
    dispatch: React.Dispatch<TableActionTypes>;
  },
  selectedStatus: FilteredStatus,
  updateSelectedValues: (selectedValues: any[], columnName: string) => void,
  isOpsAdmin: boolean
): MUIDataTableColumnDef[] => [
    {
      name: 'name',
      label: hooks.t('admin->>UserAccountManagement.Name'),
      options: {
        sort: false,
        filter: false,
        setCellProps: () => ({ style: { width: '18%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Typography variant="body2">{hooks.state.data[dataIndex].name}</Typography>
            <Typography className={hooks.classes.nricUserNameText} variant="subtitle2">
              {hooks.state.data[dataIndex].nricFin}
            </Typography>
          </>
        ),
      },
    },
    {
      name: 'accountType',
      label: hooks.t('admin->>UserAccountManagement.AccountType'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Typography variant="body2">{hooks.state.data[dataIndex].accountType || '-'}</Typography>
            {hooks.state.data[dataIndex].username &&
              (hooks.selectedRequestTypeMode === AccountTypeMode.Candidate ||
                hooks.selectedRequestTypeMode === AccountTypeMode.Coordinator) && (
                <Typography className={hooks.classes.nricUserNameText} variant="subtitle2">
                  {hooks.state.data[dataIndex].username}
                </Typography>
              )}
          </>
        ),
      },
    },
    {
      name: 'email',
      label: hooks.t('admin->>UserAccountManagement.Email'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{hooks.state.data[dataIndex].email || '-'}</Typography>
        ),
      },
    },
    {
      name: 'privateCentreSchool',
      label: hooks.t('admin->>UserAccountManagement.PrivateCentreSchool'),
      options: {
        sort: false,
        filter: true,
        filterType: 'custom',
        customFilterListOptions: customFilterOptionListRender(
          hooks.dispatch,
          hooks.state,
          'privateCentreSchool',
          selectedStatus
        ),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{hooks.state.data[dataIndex].organisation || '-'}</Typography>
        ),
        filterOptions: {
          fullWidth: true,
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <OrganizationMultiSelect
              // eslint-disable-next-line react/destructuring-assignment
              value={filterList[index]}
              label="Private Centre/School"
              filterOptionSpacing={hooks.classes.select}
              onChange={(selectedValues) => {
                updateSelectedValues(selectedValues, 'privateCentreSchool');
                // eslint-disable-next-line no-param-reassign
                filterList[index] = selectedValues.map((s) => s.code) as string[];
                onChange(filterList[index], index, column);
              }}
            />
          ),
        },
      },
    },
    {
      name: 'accountCreationDateTime',
      label: hooks.t('admin->>UserAccountManagement.AccountCreationDateTime'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">
            {hooks.state.data[dataIndex].createdAt
              ? stringToLongDateAndShortTime(hooks.state.data[dataIndex].createdAt)
              : '-'}
          </Typography>
        ),
      },
    },
    {
      name: 'expiryDate',
      label: hooks.t('admin->>UserAccountManagement.AccountExpiryDate'),
      options: {
        sort: true,
        filter: false,
        display: hooks.selectedRequestTypeMode !== AccountTypeMode.Candidate || isOpsAdmin,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{hooks.state.data[dataIndex].expiryDate || '-'}</Typography>
        ),
      },
    },
    {
      name: 'status',
      label: hooks.t('admin->>UserAccountManagement.Status'),
      options: {
        sort: false,
        filter: true,
        filterType: 'custom',
        customFilterListOptions: customFilterOptionListRender(
          hooks.dispatch,
          hooks.state,
          'status',
          selectedStatus
        ),
        customBodyRenderLite: (dataIndex: number) => {
          const { statuses } = hooks.state.data[dataIndex];
          const { t } = hooks;
          const defaultEmpty: JSX.Element[] = [];
          if (statuses != null)
            statuses.forEach((status: string) => {
              switch (status) {
                case 'LOCKED':
                  defaultEmpty.push(
                    <Pill variant="negative" label={t('admin->>AdminAccountManagement.Locked')} />
                  );
                  break;
                case 'BLOCKED':
                  defaultEmpty.push(
                    <Pill variant="negative" label={t('admin->>AdminAccountManagement.Blocked')} />
                  );
                  break;
                case 'EXPIRED':
                  defaultEmpty.push(
                    <Pill variant="negative" label={t('admin->>AdminAccountManagement.Expired')} />
                  );
                  break;
                case 'NOT ACTIVATED':
                  defaultEmpty.push(
                    <Pill variant="info" label={t('admin->>AdminAccountManagement.NotActivated')} />
                  );
                  break;
                case 'ACTIVE':
                  if (statuses.length === 1) {
                    defaultEmpty.push(
                      <Pill
                        variant="positive"
                        label={t('admin->>AdminAccountManagement.Activated')}
                      />
                    );
                  }
                  break;
                default:
                  break;
              }
            });
          return defaultEmpty.length > 0 ? (
            <>
              {defaultEmpty.map((defaultElement) => (
                <Box className={hooks.classes.pill} key={dataIndex}>
                  {defaultElement}
                </Box>
              ))}
            </>
          ) : (
            <Box className={hooks.classes.pill} key={dataIndex}>
              <Pill variant="info" label={t('admin->>AdminAccountManagement.NoAccount')} />
            </Box>
          );
        },
        filterOptions: {
          fullWidth: true,
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <AccountStatusMultiSelect
              // eslint-disable-next-line react/destructuring-assignment
              value={filterList[index]}
              label="Status"
              filterOptionSpacing={hooks.classes.select}
              isCandidate={hooks.selectedRequestTypeMode === AccountTypeMode.Candidate && !isOpsAdmin}
              onChange={(selectedValues) => {
                updateSelectedValues(selectedValues, 'status');
                // eslint-disable-next-line no-param-reassign
                filterList[index] = selectedValues.map((s) => s.code) as string[];
                onChange(filterList[index], index, column);
              }}
            />
          ),
        },
      },
    },
    {
      name: 'actions',
      label: hooks.t('admin->>UserAccountManagement.Actions'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box
            paddingTop={hooks.state.data[dataIndex].status2 ? '15px' : '0px'}
            className={hooks.classes.actionContainer}
          >
            <Button
              id="button-manage-notifications-view"
              className={hooks.classes.viewDetailsButton}
              onClick={() => {
                if (isOpsAdmin) {
                  hooks.history.push(
                    `${adminAccountManagement.route}/${hooks.state.data[dataIndex].uuid}/${viewDetails.route}`
                  );
                } else {
                  hooks.history.push(
                    `${userAccountManagement.route}/${hooks.state.data[dataIndex].uuid}/${hooks.selectedRequestTypeMode === AccountTypeMode.Candidate
                      ? 'candidate'
                      : 'coordinator'
                    }/${viewDetails.route}`
                  );
                }
              }}
              variant="text"
              color="primary"
              size="small"
            >
              {hooks.t('announcementsNotifications->>Listing.ViewDetails')}
            </Button>
          </Box>
        ),
      },
    },
  ];

const customFilterOptionListRender = (
  dispatch: React.Dispatch<TableActionTypes>,
  state: TableState,
  tableName: string,
  selectedFilters: any
): MUIDataTableCustomFilterListOptions => ({
  render: (items: any) => {
    if (selectedFilters[tableName] && selectedFilters[tableName].length && items.length > 1) {
      return [`${selectedFilters[tableName][0]}/${selectedFilters[tableName][1]}`];
    }
    if (
      selectedFilters[tableName] &&
      selectedFilters[tableName].length === 1 &&
      items.length === 1
    ) {
      return [`${selectedFilters[tableName][0]}`];
    }
    return [];
  },
  update: (filterList: string[][],
    filterPos: number,
    index: number) => {
    dispatch({
      type: 'UPDATE_FILTERS',
      payload: { ...state.filters, action: [], [tableName]: [] },
    });

    return customFilterChipRender(filterList, filterPos, index);
  },
});

interface UserAdminAccountsDataTableProps {
  selectedRequestTypeMode: AccountTypeMode;
  isOpsAdmin: boolean;
  isNoAccess: boolean;
  setIsNoAccess: React.Dispatch<React.SetStateAction<boolean>>;
  authorities: string[];
  dataTableHooks: {
    state: TableState;
    dispatch: React.Dispatch<TableActionTypes>;
    tOptions: MUIDataTableOptions;
    selectionToolbar: (
      bulkActions: React.ReactNode[] | undefined,
      onClearSelection?: (() => void) | undefined
    ) => JSX.Element;
    remoteTotalRowsSelected: number;
  };
  refreshTable: number;
}

interface FilteredStatus {
  privateCentreSchool: string[];
  status: string[];
}

const UserAdminAccountsDataTable = ({
  selectedRequestTypeMode,
  isOpsAdmin,
  isNoAccess,
  setIsNoAccess,
  authorities,
  dataTableHooks,
  refreshTable,
}: UserAdminAccountsDataTableProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const history = useHistory();
  const { state, dispatch: tableDispatch, selectionToolbar } = dataTableHooks;
  const [privateCentreSchoolSelectedValues, setPrivateCentreSchoolSelectedValues] = useState<
    CodeValueResponse[]
  >([]);
  const [statusSelectedValues, setStatusSelectedValues] = useState<KeyValueResponse[]>([]);

  const [filteredList, setFilteredList] = useState<FilteredStatus>({
    privateCentreSchool: [],
    status: [],
  });

  const updateSelectedState = (selectedValues: any[], columnName: string) => {
    if (columnName === 'status') {
      setStatusSelectedValues(selectedValues);
    }
    if (columnName === 'privateCentreSchool') {
      setPrivateCentreSchoolSelectedValues(selectedValues);
    }
  };

  const initOptions = useMemo<MUIDataTableOptions>(() => {
    /** add custom sort
     * */
    const addCustomSort = (changedColumn: string, direction: 'desc' | 'asc') => {
      tableDispatch({
        type: 'UPDATE_SORT',
        payload: {
          sortColumn: changedColumn,
          order: direction,
        },
      });
    };

    /** to handle filter confirm
     * */
    const onFilterSubmit = async (_filterList: string[][]) => {
      setFilteredList({
        privateCentreSchool: privateCentreSchoolSelectedValues.map((s) => s.codeId),
        status: statusSelectedValues.map((e) => e.description),
      });
    };

    return {
      ...dataTableHooks.tOptions,
      filter: true,
      pagination: true,
      sort: false,
      selectableRows: 'multiple',
      selectToolbarPlacement: 'none',
      onFilterConfirm: onFilterSubmit,
      onColumnSortChange: addCustomSort,
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    dataTableHooks.tOptions,
    tableDispatch,
    privateCentreSchoolSelectedValues,
    statusSelectedValues,
    state.data,
  ]);

  const selectAccountType = () => {
    if (isOpsAdmin) {
      return 'ADMINISTRATOR';
    }
    if (selectedRequestTypeMode === AccountTypeMode.Candidate) {
      return 'CANDIDATES';
    }
    return 'COORDINATORS';
  };

  const {
    mutate: mutatePostAccountManagementListing,
    isLoading: isLoadingAccountManagementListing,
  } = usePostAccountManagementListing({
    accountType: selectAccountType(),
    onSuccess: (data) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
    onError: (err) => {
      if (err?.response?.data.code === '1007') {
        setIsNoAccess(true);
        tableDispatch(({
          type: 'UPDATE_DATA',
          payload: {
            data: [],
            count: 0,
          },
        } as unknown) as TableActionTypes);
      }
    },
  });

  useEffect(
    () =>
      tableDispatch({
        type: 'UPDATE_FILTERS',
        payload: {},
      } as TableActionTypes),
    [isOpsAdmin, selectedRequestTypeMode, tableDispatch]
  );

  useEffect(() => {
    mutatePostAccountManagementListing({
      apiPathParam: {
        page: state.page,
        pageSize: state.size,
        sort: state.sortColumn,
        order: state.order,
      },
      organization: state.filters.privateCentreSchool || [],
      status: state.filters.status || [],
      q: state.search || '',
    });
    tableDispatch({ type: 'CLEAR_SELECTION' } as TableActionTypes);
  }, [
    mutatePostAccountManagementListing,
    state.order,
    state.page,
    state.size,
    state.sortColumn,
    state.search,
    state.filters,
    selectedRequestTypeMode,
    refreshTable,
    tableDispatch,
  ]);

  return (
    <Box className={classes.datatableContainer}>
      <Box className={classes.searchContainer}>
        <SearchBar
          placeholder={t('admin->>UserAccountManagement.CandidateAccountsDataTablePlaceholder')}
          name="searchInput"
          ariaLabel="searchInput"
          defaultValue={state.search}
          onSearch={(searchStr: string) => {
            tableDispatch({
              type: 'UPDATE_SEARCH',
              payload: searchStr.trim(),
            });
          }}
        />
      </Box>

      <MUIDataTable
        key={`${selectedRequestTypeMode}${isOpsAdmin.toString()}`}
        isLoading={isLoadingAccountManagementListing}
        state={state}
        dispatch={tableDispatch}
        columns={tableColumns(
          { selectedRequestTypeMode, state, classes, t, history, dispatch: tableDispatch },
          filteredList,
          updateSelectedState,
          isOpsAdmin
        )}
        emptyDataLabel={
          isNoAccess
            ? t(
              'components->>GenericError.We’re sorry, but you do not have access to this feature.'
            )
            : ''
        }
        components={{ TableToolbarSelect: () => selectionToolbar(undefined) }}
        data={state.data}
        options={{
          ...initOptions,
          sort: true,
          selectableRows:
            authorities.includes(AUTHORITY_NAMES.CP_ADMIN_USER_ACCOUNTS_EDIT) &&
              (isOpsAdmin || selectedRequestTypeMode === AccountTypeMode.Coordinator)
              ? initOptions.selectableRows
              : 'none',
        }}
        rowsPerPageOptions={[5, 10, 20, 40, 50]}
      />
    </Box>
  );
};

export default UserAdminAccountsDataTable;
